import { FC, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { IonButton, IonCheckbox, IonIcon, IonItem, IonLabel } from '@ionic/react';
import { close } from 'ionicons/icons';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { MixPanelEventEmitter } from '../metrics/MixPanelEventEmitter';
import { RecordingState } from './RecordingState';
import { mediaMinWidth } from '../theme/utils';

const useStyles = createUseStyles({
    tipsContainer: {
        backgroundColor: '#f8f9fa',
        borderRadius: 8,
        padding: 16,
        paddingBottom: 0,
        margin: '16px 0',
        position: 'relative',
        border: '1px solid #e9ecef',
        width: '100%',

        [mediaMinWidth.md]: {
            width: '65%'
        }
    },

    tipsHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 12
    },

    tipsTitle: {
        fontSize: 16,
        fontWeight: 600,
        color: 'var(--ion-color-dark)',
        margin: 0
    },

    dismissButton: {
        '--padding-start': '4px',
        '--padding-end': '4px',
        '--padding-top': '4px',
        '--padding-bottom': '4px',
        width: 24,
        height: 24
    },

    tipsList: {
        margin: 0,
        paddingLeft: 0,
        listStyle: 'none'
    },

    tipItem: {
        display: 'flex',
        alignItems: 'flex-start',
        marginBottom: 8,
        gap: 8,
        fontSize: 14,
        lineHeight: 1.4,
        color: 'var(--ion-color-dark)',

        '&:last-child': {
            marginBottom: 0
        }
    },

    tipIcon: {
        fontSize: 16,
        flexShrink: 0,
        marginTop: 1
    },

    tipText: {
        flex: 1
    },

    subTips: {
        marginTop: 4,
        paddingLeft: 0,
        listStyle: 'none',

        '& li': {
            fontSize: 13,
            lineHeight: 1.3,
            marginBottom: 3,
            color: 'var(--ion-color-medium)',

            '&:before': {
                content: '"- "',
                marginRight: 4
            }
        }
    },

    permanentDismissSection: {
        marginTop: -12
    },

    dismissCheckbox: {
        '--size': '16px',
        marginRight: 8
    }
});

interface IRecordingTip {
    id: string;
    icon: string;
    text: string;
    subTips?: string[];
}

interface IRecordingTipsProps {
    context?: 'at-appointment' | 'post-appointment' | 'exploring';
    onDismiss: () => void;
}

// Default tips for when no context is set (menu bar recording)
const DEFAULT_TIPS: IRecordingTip[] = [
    {
        id: 'dnd',
        icon: '🔕',
        text: 'Turn on Do Not Disturb'
    }
];

const TIPS_BY_CONTEXT: Record<string, IRecordingTip[]> = {
    'at-appointment': [
        {
            id: 'dnd',
            icon: '🔕',
            text: 'Turn on Do Not Disturb'
        },
        {
            id: 'placement',
            icon: '📱',
            text: 'Place phone on stable surface between you and the provider - keep mic clear'
        },
        {
            id: 'capture',
            icon: '🎯',
            text: 'Remember to capture:',
            subTips: [
                'Diagnosis updates and test results',
                'Medication changes (name, dose, timing)',
                'Follow-up instructions',
                'Next appointment scheduling',
                'Warning signs to watch for'
            ]
        }
    ],
    'post-appointment': [
        {
            id: 'dnd',
            icon: '🔕',
            text: 'Turn on Do Not Disturb - incoming calls will interrupt recording'
        },
        {
            id: 'recall',
            icon: '🔍',
            text: 'Try to recall:',
            subTips: [
                'Main concerns the doctor discussed',
                'New medications or changes',
                'What to do before next visit',
                'Questions you forgot to ask',
                'Referrals or tests ordered'
            ]
        }
    ],
    'exploring': [
        {
            id: 'dnd',
            icon: '🔕',
            text: 'Turn on Do Not Disturb - incoming calls will interrupt recording'
        },
        {
            id: 'talk-about',
            icon: '💭',
            text: 'You could talk about:',
            subTips: [
                'What happened today',
                'Tomorrow\'s tasks',
                'Updates for family',
                'Things to remember'
            ]
        }
    ]
};

export const RecordingTips: FC<IRecordingTipsProps> = ({ context, onDismiss }) => {
    const classes = useStyles();
    const mixpanel = useInstance(MixPanelEventEmitter);
    const recordingState = useInstance(RecordingState);

    const [permanentDismiss, setPermanentDismiss] = useState(false);

    // Use default tips (only DND) when no context is set
    const tips = context ? (TIPS_BY_CONTEXT[context] || []) : DEFAULT_TIPS;

    const handleCheckboxChange = async (checked: boolean) => {
        setPermanentDismiss(checked);
        // Save to storage for next time, but don't affect current session
        await recordingState.saveTipsDismissalPreference(checked);
    };

    const handleDismiss = () => {
        mixpanel.track('Recording Tips Dismissed', () => ({
            'permanently': permanentDismiss,
            'context': context || 'none'
        }));
        onDismiss();
    };

    return (
        <div className={classes.tipsContainer} data-id="recording-tips">
            <div className={classes.tipsHeader}>
                <h3 className={classes.tipsTitle}>Recording Tips</h3>
                <IonButton
                    fill="clear"
                    size="small"
                    className={classes.dismissButton}
                    onClick={handleDismiss}
                    data-id="dismiss-tips-button"
                >
                    <IonIcon icon={close} />
                </IonButton>
            </div>

            <ul className={classes.tipsList}>
                {tips.map((tip) => (
                    <li key={tip.id} className={classes.tipItem}>
                        <span className={classes.tipIcon}>{tip.icon}</span>
                        <div className={classes.tipText}>
                            {tip.text}
                            {tip.subTips && (
                                <ul className={classes.subTips}>
                                    {tip.subTips.map((subTip, index) => (
                                        <li key={index}>{subTip}</li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </li>
                ))}
            </ul>

            <div className={classes.permanentDismissSection}>
                <IonItem lines="none" style={{backgroundColor: 'transparent', '--background': 'transparent'}}>
                    <IonCheckbox
                        className={classes.dismissCheckbox}
                        checked={permanentDismiss}
                        onIonChange={(e) => handleCheckboxChange(e.detail.checked)}
                        data-id="permanent-dismiss-checkbox"
                    />
                    <IonLabel>
                        <span style={{fontSize: 14, color: 'var(--ion-color-dark)'}}>
                            Don't show tips again
                        </span>
                    </IonLabel>
                </IonItem>
            </div>
        </div>
    );
};
